import React from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Users, Stethoscope, ShieldCheck, Lock, Handshake } from "lucide-react";
import { useApp } from "@/context/AppContext";

/**
 * /sobre-nosotros — fully localized ES / FR / EN.
 * Layout mirrors the desktop mockup: hero + two audience cards + full-width
 * blue "compromiso" banner + trust & security + team/contact + final CTA.
 * The brand name "Agendik" and the term "Socio referidor" are intentionally
 * kept untranslated across all three languages (Agendik legal convention).
 */
const T = {
  es: {
    seoTitle: "Sobre nosotros",
    seoDesc: "Agendik es la plataforma de citas médicas en línea de Ecuador. Los pacientes reservan en 2 clics, los médicos gestionan su consultorio desde su teléfono. Médicos verificados en la SENESCYT, datos protegidos según la LOPDP.",
    eyebrow: "Sobre nosotros",
    heroTitle: "Conectamos a pacientes y médicos en Ecuador",
    heroPara: "Agendik es la plataforma de citas médicas en línea donde los pacientes reservan su cita en pocos clics, las 24 horas, y los médicos gestionan su consultorio desde su teléfono.",
    patientsTitle: "Para pacientes",
    patientsPara: "Encuentre un médico verificado en su ciudad, vea sus horarios disponibles y reserve sin llamar. Gratis.",
    ctaSearch: "Buscar médico",
    doctorsTitle: "Para médicos",
    doctorsPara: "Agenda en línea, recordatorios automáticos y un perfil visible en internet para recibir nuevos pacientes.",
    ctaDoctor: "Soy médico",
    commitEyebrow: "Nuestro compromiso",
    commit: "Ofrecer un servicio de calidad, ágil y siempre al servicio de los médicos.",
    trustTitle: "Confianza y seguridad",
    verifiedTitle: "Médicos verificados",
    verifiedPara: "Verificamos el título de cada médico registrado en la SENESCYT.",
    privacyTitle: "Datos protegidos",
    privacyPara: "Protegemos los datos de pacientes y médicos conforme a la Ley Orgánica de Protección de Datos Personales del Ecuador.",
    teamTitle: "Quiénes somos",
    teamPara: <>Agendik es un servicio de <strong>A Good Job</strong>, con un equipo en Ecuador que acompaña a los médicos en su consultorio.</>,
    contactTitle: "Contacto",
    contactLead: "Para cualquier información o sugerencia sobre agendik.io, puede escribirnos a:",
    finalTitle: "¿Listo para empezar?",
    partnerTitle: "Recomiende Agendik y gane cada mes",
    partnerPara: "¿Conoce médicos que necesitan más pacientes y una agenda ordenada? Recomiéndeles Agendik. Por cada médico referido que paga su suscripción (después de su mes gratis), usted recibe $9 USD al mes, transferidos a su cuenta bancaria en Ecuador.",
    partnerCta: "Quiero ser socio referidor",
    finalPartnerCta: "Quiero ser socio referidor",
  },
  fr: {
    seoTitle: "À propos de nous",
    seoDesc: "Agendik est la plateforme de rendez-vous médicaux en ligne de l'Équateur. Les patients réservent en 2 clics, les médecins gèrent leur cabinet depuis leur téléphone. Médecins vérifiés auprès du SENESCYT, données protégées selon la LOPDP.",
    eyebrow: "À propos",
    heroTitle: "Nous connectons patients et médecins en Équateur",
    heroPara: "Agendik est la plateforme de rendez-vous médicaux en ligne où les patients prennent rendez-vous en quelques clics, 24h/24, et où les médecins gèrent leur cabinet depuis leur téléphone.",
    patientsTitle: "Pour les patients",
    patientsPara: "Trouvez un médecin vérifié dans votre ville, consultez ses créneaux disponibles et réservez sans téléphoner. Gratuit.",
    ctaSearch: "Trouver un médecin",
    doctorsTitle: "Pour les médecins",
    doctorsPara: "Agenda en ligne, rappels automatiques et un profil visible sur Internet pour recevoir de nouveaux patients.",
    ctaDoctor: "Je suis médecin",
    commitEyebrow: "Notre engagement",
    commit: "Offrir un service de qualité, rapide et toujours au service des médecins.",
    trustTitle: "Confiance et sécurité",
    verifiedTitle: "Médecins vérifiés",
    verifiedPara: "Nous vérifions le diplôme de chaque médecin inscrit auprès du SENESCYT.",
    privacyTitle: "Données protégées",
    privacyPara: "Nous protégeons les données des patients et des médecins conformément à la Loi organique de protection des données personnelles de l'Équateur.",
    teamTitle: "Qui sommes-nous",
    teamPara: <>Agendik est un service d'<strong>A Good Job</strong>, avec une équipe en Équateur qui accompagne les médecins au quotidien dans leur cabinet.</>,
    contactTitle: "Contact",
    contactLead: "Pour toute information ou suggestion concernant agendik.io, écrivez-nous à :",
    finalTitle: "Prêt à commencer ?",
    partnerTitle: "Recommandez Agendik et gagnez chaque mois",
    partnerPara: "Vous connaissez des médecins qui ont besoin de plus de patients et d'un agenda bien organisé ? Recommandez-leur Agendik. Pour chaque médecin parrainé qui paie son abonnement (après son mois gratuit), vous recevez $9 USD par mois, versés par virement sur votre compte bancaire en Équateur.",
    partnerCta: "Je veux devenir socio referidor",
    finalPartnerCta: "Devenir partenaire référent",
  },
  en: {
    seoTitle: "About us",
    seoDesc: "Agendik is Ecuador's online medical appointment platform. Patients book in 2 clicks, doctors manage their practice from their phone. Doctors verified with SENESCYT, data protected under the LOPDP.",
    eyebrow: "About us",
    heroTitle: "We connect patients and doctors in Ecuador",
    heroPara: "Agendik is the online medical appointment platform where patients book in a few clicks, 24/7, and doctors manage their practice from their phone.",
    patientsTitle: "For patients",
    patientsPara: "Find a verified doctor in your city, see their available slots and book without calling. Free.",
    ctaSearch: "Find a doctor",
    doctorsTitle: "For doctors",
    doctorsPara: "Online schedule, automatic reminders and a profile visible online to receive new patients.",
    ctaDoctor: "I'm a doctor",
    commitEyebrow: "Our commitment",
    commit: "To deliver a high-quality, fast service, always at the service of doctors.",
    trustTitle: "Trust & security",
    verifiedTitle: "Verified doctors",
    verifiedPara: "We verify every registered doctor's diploma with SENESCYT.",
    privacyTitle: "Protected data",
    privacyPara: "We protect patient and doctor data in accordance with Ecuador's Organic Law on the Protection of Personal Data.",
    teamTitle: "Who we are",
    teamPara: <>Agendik is a service by <strong>A Good Job</strong>, with a team in Ecuador supporting doctors in their day-to-day practice.</>,
    contactTitle: "Contact",
    contactLead: "For any information or suggestions about agendik.io, you can write to us at:",
    finalTitle: "Ready to start?",
    partnerTitle: "Recommend Agendik and earn every month",
    partnerPara: "Do you know doctors who need more patients and a well-organized schedule? Recommend Agendik to them. For each referred doctor who pays their subscription (after their free month), you receive $9 USD per month, transferred to your bank account in Ecuador.",
    partnerCta: "I want to become a socio referidor",
    finalPartnerCta: "I want to be a referral partner",
  },
};

export default function AboutUs() {
  const { lang } = useApp();
  const t = T[lang] || T.es;
  return (
    <div>
      <Seo title={t.seoTitle} description={t.seoDesc}/>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 pt-12 pb-6">
        <p className="text-sm font-semibold tracking-widest uppercase text-blue-600 mb-3" data-testid="about-eyebrow">
          {t.eyebrow}
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight mb-5 max-w-3xl">
          {t.heroTitle}
        </h1>
        <p className="text-lg text-slate-600 max-w-3xl leading-relaxed">{t.heroPara}</p>
      </section>

      <section data-testid="about-partner-block" className="max-w-6xl mx-auto px-4 lg:px-8 pt-10">
        <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-8">
          <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mb-5" data-testid="about-partner-icon">
            <Handshake size={24} className="text-emerald-600" aria-hidden="true"/>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-3">{t.partnerTitle}</h2>
          <p className="text-slate-700 leading-relaxed mb-6 max-w-3xl" data-testid="about-partner-text">{t.partnerPara}</p>
          <Link to="/socio/registro" data-testid="about-partner-cta"
                className="inline-flex items-center justify-center min-h-11 py-2.5 text-center px-6 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition">
            {t.partnerCta}
          </Link>
        </div>
      </section>

      {/* TWO AUDIENCE CARDS */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div data-testid="about-card-doctors"
               className="rounded-2xl bg-blue-600 p-8 flex flex-col text-white">
            <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center mb-5">
              <Stethoscope size={24} className="text-white" aria-hidden="true"/>
            </div>
            <h2 className="text-2xl font-bold mb-3">{t.doctorsTitle}</h2>
            <p className="text-white/90 leading-relaxed mb-6 flex-1">{t.doctorsPara}</p>
            <Link to="/para-medicos"
                  data-testid="about-cta-doctors"
                  className="inline-flex items-center justify-center h-11 px-6 rounded-xl bg-white hover:bg-blue-50 text-blue-700 font-semibold transition self-start">
              {t.ctaDoctor}
            </Link>
          </div>

          <div data-testid="about-card-patients"
               className="rounded-2xl bg-blue-50 border border-blue-100 p-8 flex flex-col">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-5">
              <Users size={24} className="text-blue-600" aria-hidden="true"/>
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">{t.patientsTitle}</h2>
            <p className="text-slate-700 leading-relaxed mb-6 flex-1">{t.patientsPara}</p>
            <Link to="/buscar"
                  data-testid="about-cta-search"
                  className="inline-flex items-center justify-center h-11 px-6 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold transition self-start">
              {t.ctaSearch}
            </Link>
          </div>
        </div>
      </section>

      {/* COMMITMENT BANNER */}
      <section aria-labelledby="about-commitment-title" className="bg-blue-700 text-white">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-14 text-center">
          <p id="about-commitment-title"
             className="text-sm font-semibold tracking-widest uppercase text-blue-200 mb-4">
            {t.commitEyebrow}
          </p>
          <p data-testid="about-commitment"
             className="text-2xl sm:text-3xl font-bold leading-snug max-w-3xl mx-auto">
            {t.commit}
          </p>
        </div>
      </section>

      {/* TRUST & SECURITY */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 py-16">
        <h2 className="text-3xl font-bold text-slate-900 mb-8">{t.trustTitle}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div data-testid="about-trust-verified" className="flex gap-4">
            <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
              <ShieldCheck size={22} className="text-blue-600" aria-hidden="true"/>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{t.verifiedTitle}</h3>
              <p className="text-slate-600 leading-relaxed">{t.verifiedPara}</p>
            </div>
          </div>
          <div data-testid="about-trust-privacy" className="flex gap-4">
            <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
              <Lock size={22} className="text-blue-600" aria-hidden="true"/>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{t.privacyTitle}</h3>
              <p className="text-slate-600 leading-relaxed">{t.privacyPara}</p>
            </div>
          </div>
        </div>
      </section>

      {/* TEAM + CONTACT */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div data-testid="about-team">
            <h2 className="text-2xl font-bold text-slate-900 mb-3">{t.teamTitle}</h2>
            <p className="text-slate-700 leading-relaxed">{t.teamPara}</p>
          </div>
          <div data-testid="about-contact">
            <h2 className="text-2xl font-bold text-blue-600 mb-3">{t.contactTitle}</h2>
            <p className="text-slate-700 leading-relaxed mb-1">{t.contactLead}</p>
            <a href="mailto:agoodjobsasu@gmail.com" data-testid="about-contact-email"
               className="text-blue-600 font-semibold hover:underline">
              agoodjobsasu@gmail.com
            </a>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section aria-labelledby="about-final-cta" className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-14 text-center">
          <h2 id="about-final-cta" className="text-3xl sm:text-4xl font-bold text-slate-900 mb-8">
            {t.finalTitle}
          </h2>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/para-medicos"
                  data-testid="about-final-cta-doctor"
                  className="inline-flex items-center justify-center h-12 px-7 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold transition">
              {t.ctaDoctor}
            </Link>
            <Link to="/socio/registro"
                  data-testid="about-final-cta-partner"
                  className="inline-flex items-center justify-center h-12 px-7 rounded-xl border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 font-semibold transition">
              {t.finalPartnerCta}
            </Link>
            <Link to="/buscar"
                  data-testid="about-final-cta-patient"
                  className="inline-flex items-center justify-center h-12 px-7 rounded-xl border-2 border-blue-600 text-blue-700 hover:bg-blue-50 font-semibold transition">
              {t.ctaSearch}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
